import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, Play, Printer, ArrowLeft, Award, ListChecks, Zap } from "lucide-react";

export default function SamplePapers() {
  const { classId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/sample-papers/${classId}`).then((r) => setData(r.data));
  }, [classId]);

  if (!data) return <div className="text-zinc-400">Loading sample papers…</div>;

  return (
    <div className="max-w-4xl mx-auto fade-up">
      <Link to={`/classes/${classId}`} className="text-xs text-zinc-400 hover:text-orange-300 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to class
      </Link>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Sample Papers · {data.class_label}</div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight gradient-sunset-text" data-testid="sample-papers-title">
          Board-style Papers &amp; Mock Tests
        </h1>
        <p className="text-zinc-300 mt-2 max-w-2xl">
          Official CBSE Sample Question Papers (where available) and instant mock tests generated from every chapter — practice with the real exam format.
        </p>
      </div>

      {/* Full-length mock — BYJU'S-style multi-section paper */}
      <section className="mb-10 rounded-xl border border-orange-300/25 bg-gradient-to-br from-pink-300/[0.08] via-orange-300/[0.05] to-yellow-300/[0.05] p-6" data-testid="full-mock-cta">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-300 via-orange-300 to-yellow-300 grid place-items-center shrink-0">
              <Zap className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-orange-300 mb-1">New — Full mock paper</div>
              <div className="font-display text-lg font-semibold text-zinc-100">Class-wide full mock — 30 questions, 4 sections</div>
              <p className="text-sm text-zinc-400 mt-1 max-w-lg">
                Byju's-style paper: mixed short/long/objective questions drawn from every chapter of {data.class_label}. Fresh mix on each attempt. Full step-by-step solutions on submit.
              </p>
            </div>
          </div>
          <Link to={`/full-mock/${classId}`}>
            <Button className="bg-sky-400 hover:bg-sky-300 text-slate-900 rounded-full font-semibold" data-testid="cta-full-mock">
              <Play className="w-4 h-4 mr-1" /> Start full mock
            </Button>
          </Link>
        </div>
      </section>

      {/* Official CBSE Sample Papers */}
      {data.has_official ? (
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-yellow-300" />
            <h2 className="font-display text-lg font-semibold text-zinc-100">Official CBSE Sample Papers ({data.class_label})</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.official.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="lift group rounded-xl border border-pink-300/20 bg-gradient-to-br from-pink-300/[0.06] via-transparent to-orange-300/[0.06] p-5 block"
                data-testid={`official-paper-${i}`}
              >
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-yellow-300 mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-pink-300 mb-1">{p.type}</div>
                    <div className="font-display text-base font-semibold text-zinc-100 group-hover:gradient-sunset-text transition-colors">
                      {p.title}
                    </div>
                    <div className="text-xs text-zinc-400 mt-2 flex gap-3">
                      <span>{p.marks} marks</span>
                      <span>•</span>
                      <span>{p.duration}</span>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-orange-300 shrink-0" />
                </div>
              </a>
            ))}
          </div>
          {data.archive_url && (
            <a
              href={data.archive_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1 text-xs text-orange-300 hover:text-yellow-300"
              data-testid="sqp-archive-link"
            >
              Full sample-paper archive on cbseacademic.nic.in <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </section>
      ) : (
        <section className="mb-10 rounded-xl border border-yellow-300/20 bg-yellow-300/5 p-5">
          <div className="flex items-start gap-3">
            <Award className="w-5 h-5 text-yellow-300 mt-1 shrink-0" />
            <div>
              <div className="font-display text-base font-semibold text-zinc-100 mb-1">No board-issued sample papers for {data.class_label}</div>
              <p className="text-sm text-zinc-400">
                CBSE only publishes Sample Question Papers for Class X and Class XII (board exam classes).
                Use our chapter-wise mock tests below to prep at the same exam pace.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Chapter-wise mock tests */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <ListChecks className="w-4 h-4 text-pink-300" />
          <h2 className="font-display text-lg font-semibold text-zinc-100">Chapter-wise Mock Tests</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Instant 10-question tests drawn from each chapter's exercise bank. Ideal for spaced revision.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.mock_papers.map((mp) => (
            <div key={mp.chapter_id} className="rounded-xl border border-white/10 bg-[#152040] hover:border-orange-300/40 p-4 transition-colors" data-testid={`mock-${mp.chapter_id}`}>
              <div className="text-[10px] uppercase tracking-widest text-pink-300 mb-1">
                Chapter {mp.chapter_id.split("-")[1]}
              </div>
              <div className="font-display text-base font-semibold text-zinc-100 mb-1">{mp.chapter}</div>
              <div className="text-xs text-zinc-400 mb-3">{mp.questions} questions available</div>
              <div className="flex gap-2">
                <Link to={mp.practice_url} className="flex-1">
                  <Button size="sm" className="w-full bg-sky-400 hover:bg-sky-300 text-slate-900 rounded-full font-semibold" data-testid={`take-${mp.chapter_id}`}>
                    <Play className="w-3.5 h-3.5 mr-1" /> Take test
                  </Button>
                </Link>
                <Link to={mp.worksheet_url}>
                  <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">
                    <Printer className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
